// Who is behind? Pure counting on top of auditDueFor (src/lib/auditDue.js),
// used by the Chair's Trainee Audit panel and the Committee Review menu dot.
// No fetching here. Same cadence, same "due soon = 14 days" rule as the page.
import { auditDueFor } from "./auditDue.js";
import { isLicensedDeputy } from "./traineeKind.js";

// Counts for a list of people against one cadence.
// Returns { total, overdue, soon, ok, unknown, oldestOverdueDays }
// oldestOverdueDays is null when nothing is overdue.
export function dueCounts(people, { verifications, records, pilotCreated = {}, months, now = new Date() }) {
  const out = { total: 0, overdue: 0, soon: 0, ok: 0, unknown: 0, oldestOverdueDays: null };
  for (const t of people || []) {
    const d = auditDueFor({ trainee: t, verifications, records, pilotCreatedAt: pilotCreated[t.id], months, now });
    out.total += 1;
    out[d.status] += 1;
    if (d.status === "overdue") {
      const days = -d.daysLeft;
      if (out.oldestOverdueDays === null || days > out.oldestOverdueDays) out.oldestOverdueDays = days;
    }
  }
  return out;
}

// The number on the Committee Review menu item: overdue plus due within 14 days.
export function needsAttentionCount(people, ctx) {
  const c = dueCounts(people, ctx);
  return c.overdue + c.soon;
}

// Pilots holding the auditor position who are still active (retired pilots can
// keep pilot_positions rows). Returns [{ id, name }].
export function auditorRoster(pilots, committeeByPilot, nameOf) {
  return (pilots || [])
    .filter((p) => p.is_active !== false && (committeeByPilot?.[p.id] || []).includes("auditor"))
    .map((p) => ({ id: p.id, name: nameOf(p) }));
}

// The Chair's "who is behind" summary.
//   people:           everyone on the Trainee Audit (trainees and deputies)
//   auditorByTrainee: trainee id -> auditor pilot id
//   auditors:         [{ id, name }] pilots holding the auditor position, so an
//                     auditor with nobody assigned still shows a row
//   nameOf:           pilot id -> name, for an assigned auditor not in `auditors`
// Returns { auditors: [{ id, name, assigned, overdue, soon, oldestOverdueDays }],
//           unassigned: n, deputiesOverdue: n, traineesNoFinalPass: n }
export function committeeSummary({ people, auditorByTrainee = {}, auditors = [], nameOf = () => "unknown", verifications, records, pilotCreated = {}, months, now = new Date() }) {
  const trainees = (people || []).filter((t) => !isLicensedDeputy(t));
  const deputies = (people || []).filter(isLicensedDeputy);
  const ctx = { verifications, records, pilotCreated, months, now };

  const groups = new Map();
  for (const a of auditors) groups.set(a.id, { name: a.name, list: [] });
  let unassigned = 0;
  for (const t of trainees) {
    const aid = auditorByTrainee[t.id];
    if (!aid) { unassigned += 1; continue; }
    if (!groups.has(aid)) groups.set(aid, { name: nameOf(aid), list: [] });
    groups.get(aid).list.push(t);
  }
  const rows = [...groups.entries()].map(([id, g]) => {
    const c = dueCounts(g.list, ctx);
    return { id, name: g.name, assigned: c.total, overdue: c.overdue, soon: c.soon, oldestOverdueDays: c.oldestOverdueDays };
  }).sort((a, b) => String(a.name).localeCompare(String(b.name)));

  const hasFinal = new Set((verifications || []).filter((v) => v.kind === "final_pass").map((v) => v.trainee_id));
  return {
    auditors: rows,
    unassigned,
    deputiesOverdue: dueCounts(deputies, ctx).overdue,
    traineesNoFinalPass: trainees.filter((t) => !hasFinal.has(t.id)).length,
  };
}
